import { gql } from '@apollo/client';

export const LOGIN = gql`
  mutation AdminLogin(
    $email: String!
    $password: String!
    $mfaToken: String
    $mfaBackupCode: String
  ) {
    signIn(
      login: $email
      password: $password
      mfaToken: $mfaToken
      mfaBackupCode: $mfaBackupCode
    ) {
      user {
        id
        email
        role
        mfaEnabled
      }
    }
  }
`;

export const ME = gql`
  query AdminMe {
    me {
      id
      email
      role
      mfaEnabled
    }
  }
`;

export const ADMIN_KPI_SUMMARY = gql`
  query AdminKpiSummary($range: AdminMetricsRange) {
    adminKpiSummary(range: $range) {
      totalUsers
      signups
      wau
      violations
      asOf
      series {
        date
        signups
        activeUsers
        totalUsers
        violations
      }
    }
  }
`;

export const ADMIN_SIGNUP_SERIES = gql`
  query AdminSignupSeries($range: AdminMetricsRange) {
    adminSignupSeries(range: $range, granularity: DAILY) {
      date
      count
    }
  }
`;

export const ADMIN_MRR_SUMMARY = gql`
  query AdminMrrSummary($range: AdminAnalyticsRangeInput) {
    adminMrrSummary(range: $range) {
      asOf
      mrrCents
      activeSubscriptions
      currency
      planBreakdown {
        tierSlug
        interval
        count
        mrrCents
      }
    }
  }
`;

export const ADMIN_BANK_LINK_ECONOMICS_SUMMARY = gql`
  query AdminBankLinkEconomicsSummary {
    adminBankLinkEconomicsSummary {
      asOf
      activeItems
      activeLinkers
      estimatedBalanceWavesMo
      linkersByTier {
        tierSlug
        users
        items
      }
    }
  }
`;

export const ADMIN_TIER_DISTRIBUTION = gql`
  query AdminTierDistribution($includeInactive: Boolean) {
    adminTierDistribution(includeInactive: $includeInactive) {
      tier
      slug
      count
    }
  }
`;

export const ADMIN_TIER_VIOLATION_STATS = gql`
  query AdminTierViolationStats(
    $range: AdminMetricsRange
    $reason: String
  ) {
    adminTierViolationStats(range: $range, reason: $reason) {
      total
      byAction
      byReason
      byTier
    }
  }
`;

export const ADMIN_USER_SEARCH = gql`
  query AdminUserSearch($query: String!) {
    adminUserSearch(query: $query) {
      id
      email
      role
      tierSlug
      tierName
      isActive
      createdAt
      signupAcquisition {
        referrer
        landingPage
        utmSource
        utmMedium
        utmCampaign
        utmContent
        utmTerm
        gclid
        fbclid
        cam
        signupSource
      }
    }
  }
`;

/** Fallback when the API schema predates website-api !410 (no SignupAcquisition). */
export const ADMIN_USER_SEARCH_CORE = gql`
  query AdminUserSearchCore($query: String!) {
    adminUserSearch(query: $query) {
      id
      email
      role
      tierSlug
      tierName
      isActive
      createdAt
    }
  }
`;

export const ADMIN_USERS = gql`
  query AdminUsers(
    $sort: AdminUserSort
    $createdOn: Date
    $lastLoginOn: Date
    $limit: Int
    $offset: Int
  ) {
    adminUsers(
      sort: $sort
      createdOn: $createdOn
      lastLoginOn: $lastLoginOn
      limit: $limit
      offset: $offset
    ) {
      total
      limit
      offset
      users {
        id
        email
        role
        tierSlug
        tierName
        isActive
        createdAt
        lastLogin
        signupAcquisition {
          referrer
          landingPage
          utmSource
          utmMedium
          utmCampaign
          utmContent
          utmTerm
          gclid
          fbclid
          cam
          signupSource
        }
      }
    }
  }
`;

export const ADMIN_USER_TIER_OVERRIDES = gql`
  query AdminUserTierOverrides($userId: ID!) {
    adminUserTierOverrides(userId: $userId) {
      id
      userId
      limitType
      overrideValue
      effectiveTierSlug
      reason
      expiresAt
    }
  }
`;

export const ADMIN_USER_EFFECTIVE_LIMITS = gql`
  query AdminUserEffectiveLimits($userId: ID!) {
    adminUserEffectiveLimits(userId: $userId) {
      userId
      effectiveTier
      accounts
      entities
      teamMembers
    }
  }
`;

export const ADMIN_SET_USER_TIER_OVERRIDE = gql`
  mutation AdminSetUserTierOverride(
    $userId: ID!
    $limitType: String!
    $overrideValue: Int
    $effectiveTierSlug: String
    $reason: String
    $expiresAt: String
  ) {
    adminSetUserTierOverride(
      userId: $userId
      limitType: $limitType
      overrideValue: $overrideValue
      effectiveTierSlug: $effectiveTierSlug
      reason: $reason
      expiresAt: $expiresAt
    ) {
      id
      limitType
      overrideValue
      effectiveTierSlug
      reason
      expiresAt
    }
  }
`;

export const ADMIN_REMOVE_USER_TIER_OVERRIDE = gql`
  mutation AdminRemoveUserTierOverride($userId: ID!, $limitType: String!) {
    adminRemoveUserTierOverride(userId: $userId, limitType: $limitType)
  }
`;

export const ADMIN_AUDIT_LOG = gql`
  query AdminAuditLog($limit: Int, $offset: Int, $action: String, $targetId: ID) {
    adminAuditLog(limit: $limit, offset: $offset, action: $action, targetId: $targetId) {
      total
      limit
      offset
      entries {
        id
        actorId
        action
        targetType
        targetId
        metadata
        createdAt
      }
    }
  }
`;


export const ADMIN_JOB_STATUS = gql`
  query AdminJobStatus {
    adminJobStatus {
      rollupAsOf
      stripeCacheAsOf
      rollupStatus
      stripeCacheStatus
      latestRollupDate
      rollupJobName
      stripeCacheJobName
    }
  }
`;

export const ADMIN_MRR_SERIES = gql`
  query AdminMrrSeries($range: AdminAnalyticsRangeInput) {
    adminMrrSeries(range: $range) {
      asOf
      mrrCents
      activeSubscriptions
    }
  }
`;

export const ADMIN_VIOLATION_COUNTS_BY_DAY = gql`
  query AdminViolationCountsByDay($range: AdminMetricsRange) {
    adminViolationCountsByDay(range: $range) {
      date
      count
    }
  }
`;

export const ADMIN_SIGNUPS_BY_TIER_IN_RANGE = gql`
  query AdminSignupsByTierInRange($range: AdminMetricsRange) {
    adminSignupsByTierInRange(range: $range) {
      tier
      slug
      count
    }
  }
`;

export const ADMIN_SIGNUPS_BY_TIER_SERIES = gql`
  query AdminSignupsByTierSeries($range: AdminMetricsRange!) {
    adminSignupsByTierSeries(range: $range) {
      date
      slug
      count
    }
  }
`;

export const ADMIN_SIGNUP_SOURCES = gql`
  query AdminSignupSources($range: AdminMetricsRange) {
    adminSignupSources(range: $range) {
      source
      count
    }
  }
`;

export const ADMIN_PAID_SIGNUPS_BY_SOURCE = gql`
  query AdminPaidSignupsBySource($range: AdminMetricsRange) {
    adminPaidSignupsBySource(range: $range) {
      source
      signups
      paid
    }
  }
`;

export const ADMIN_ACTIVATION_FUNNEL = gql`
  query AdminActivationFunnel($range: AdminMetricsRange) {
    adminActivationFunnel(range: $range) {
      id
      count
    }
  }
`;

export const ADMIN_PAYMENT_FAIL_SUMMARY = gql`
  query AdminPaymentFailSummary($range: AdminMetricsRange) {
    adminPaymentFailSummary(range: $range) {
      count
    }
  }
`;

export const ADMIN_PAYMENT_FAILS = gql`
  query AdminPaymentFails($range: AdminMetricsRange) {
    adminPaymentFails(range: $range) {
      capped
      rows {
        userId
        attemptCount
        failedAt
      }
    }
  }
`;

export const ADMIN_REFRESH_STRIPE_CACHE = gql`
  mutation AdminRefreshStripeCache {
    adminRefreshStripeCache {
      success
      asOf
      mrrCents
      activeSubscriptions
    }
  }
`;

export const ADMIN_REFRESH_DAILY_ROLLUP = gql`
  mutation AdminRefreshDailyRollup {
    adminRefreshDailyRollup {
      success
      asOf
      latestRollupDate
      processed
    }
  }
`;

export const ADMIN_LOG_USER_VIEW = gql`
  mutation AdminLogUserView($userId: ID!) {
    adminLogUserView(userId: $userId)
  }
`;

export const ADMIN_PRODUCT_ANALYTICS = gql`
  query AdminProductAnalytics($range: AdminMetricsRange) {
    adminProductAnalytics(range: $range) {
      from
      to
      source
      pageViews {
        pagePath
        dimension
        count
      }
      emptyStates {
        pagePath
        dimension
        count
      }
      formBlocked {
        pagePath
        dimension
        count
      }
      featureGates {
        pagePath
        dimension
        count
      }
      funnel {
        pagePath
        dimension
        count
      }
    }
  }
`;

export const ADMIN_PRODUCT_PATHS = gql`
  query AdminProductPaths($range: AdminMetricsRange) {
    adminProductAnalytics(range: $range) {
      journeys {
        path
        channel
        count
        steps
      }
      pathPairs {
        fromPath
        toPath
        channel
        count
      }
      serverConversions {
        pagePath
        dimension
        count
      }
    }
  }
`;

export const ADMIN_VISIT_SESSIONS = gql`
  query AdminVisitSessions($range: AdminMetricsRange) {
    adminVisitSessions(range: $range) {
      startedAt
      endedAt
      email
      displayName
      channel
      hops {
        path
        at
      }
      interactions {
        at
        eventName
        path
      }
    }
  }
`;

export const ADMIN_PAGE_HEATMAP = gql`
  query AdminPageHeatmap(
    $path: String
    $range: AdminMetricsRange
    $device: String
    $channel: String
  ) {
    adminPageHeatmap(path: $path, range: $range, device: $device, channel: $channel) {
      from
      to
      pagePath
      minCount
      clicks {
        xBucket
        yBucket
        count
      }
      scroll {
        depthBucket
        count
      }
      selectors {
        selector
        count
      }
      paths {
        pagePath
        count
      }
    }
  }
`;

export const ADMIN_VISIT_TIMELINES = gql`
  query AdminVisitTimelines(
    $path: String!
    $channel: String
    $range: AdminMetricsRange
    $limit: Int
  ) {
    adminVisitTimelines(path: $path, channel: $channel, range: $range, limit: $limit) {
      id
      hops {
        eventName
        path
      }
    }
  }
`;

export const ADMIN_DECISIONS_FUNNEL = gql`
  query AdminDecisionsFunnel($range: AdminMetricsRange) {
    adminDecisionsFunnel(range: $range) {
      stepId
      label
      targetPercent
      actualPercent
    }
  }
`;

export const ADMIN_EXPERIMENTS = gql`
  query AdminExperiments {
    adminExperiments {
      key
      name
      description
      surface
      pagePath
      status
      statusSource
      primaryGoal
      primaryGoalLabel
      winnerRule
      plannedExposuresPerArm
      decision
      decisionReady
      srmFlagged
      srmChecked
      mutexConflict
      exposures
      conversions
      conversionRate
      updatedAt
      guardrails {
        goal
        label
        failing
        variants {
          variantKey
          conversions
          rate
        }
      }
      variants {
        variantKey
        label
        exposures
        conversions
        conversionRate
        rateLow
        rateHigh
        lift
        liftLow
        liftHigh
      }
      pageMounted
      exposureSeries {
        date
        exposures
        conversions
      }
      statusHistory {
        at
        from
        to
      }
    }
  }
`;

export const ADMIN_SET_EXPERIMENT_STATUS = gql`
  mutation AdminSetExperimentStatus($key: String!, $status: AdminExperimentStatus!) {
    adminSetExperimentStatus(key: $key, status: $status) {
      key
      status
      statusSource
      updatedAt
    }
  }
`;

export const ADMIN_CREATE_EXPERIMENT = gql`
  mutation AdminCreateExperiment($key: String!, $draft: AdminExperimentDraftInput!) {
    adminCreateExperiment(key: $key, draft: $draft) {
      key
      name
      status
    }
  }
`;

export const ADMIN_UPDATE_EXPERIMENT_DRAFT = gql`
  mutation AdminUpdateExperimentDraft($key: String!, $draft: AdminExperimentDraftInput!) {
    adminUpdateExperimentDraft(key: $key, draft: $draft) {
      key
      name
      status
    }
  }
`;

const ADMIN_IHVG_PACK_FIELDS = gql`
  fragment AdminIhvgPackFields on AdminIhvgPack {
    id
    slug
    status
    caption
    cta
    proofIds
    mediaObjectKey
    mediaSha256
    previewUrl
    snapshot
    publishStatus
    audio {
      mode
      bedAssetId
      bedLicenseRef
      originalAudioVolume
    }
    platformSounds {
      tiktokMusicSoundId
      tiktokCmlNote
      instagramAudioId
      facebookSoundCollectionNote
      youtubeNote
      linkedinNote
      nativeUiAfterSchedule
      robinReachPassthrough
    }
    platformSoundsChecklist
    contentFamily
    contentVariant
    contentVariantLabel
    contentFormat
    burnOverlay
    stockBedId
    overlayStyle {
      fontId
      fillColor
      outlineColor
    }
    registryKey
    createdBy
    composeError
    createdAt
    updatedAt
  }
`;

export const ADMIN_IHVG_PACKS = gql`
  ${ADMIN_IHVG_PACK_FIELDS}
  query AdminIhvgPacks {
    adminIhvgPacks {
      ...AdminIhvgPackFields
    }
  }
`;

export const ADMIN_IHVG_PACK = gql`
  ${ADMIN_IHVG_PACK_FIELDS}
  query AdminIhvgPack($id: ID!) {
    adminIhvgPack(id: $id) {
      ...AdminIhvgPackFields
    }
  }
`;

export const ADMIN_IHVG_PROOF_CATALOG = gql`
  query AdminIhvgProofCatalog {
    adminIhvgProofCatalog {
      id
      objectKey
      label
      surface
      source
    }
  }
`;

export const ADMIN_IHVG_STOCK_CATALOG = gql`
  query AdminIhvgStockCatalog {
    adminIhvgStockCatalog {
      id
      objectKey
      label
      kind
      license
    }
  }
`;

export const ADMIN_IHVG_CAPTION_IDEAS = gql`
  query AdminIhvgCaptionIdeas(
    $family: String
    $format: String
    $query: String
    $limit: Int
  ) {
    adminIhvgCaptionIdeas(
      family: $family
      format: $format
      query: $query
      limit: $limit
    ) {
      id
      label
      family
      format
      caption
      cta
      hook
    }
  }
`;

export const ADMIN_IHVG_CLIP_PRESETS = gql`
  query AdminIhvgClipPresets {
    adminIhvgClipPresets {
      id
      label
      proofId
      suggestedCaption
      suggestedCta
    }
  }
`;

export const ADMIN_IHVG_AUDIO_BEDS = gql`
  query AdminIhvgAudioBeds {
    adminIhvgAudioBeds {
      id
      objectKey
      label
      licenseRef
      mime
      filename
    }
  }
`;

export const ADMIN_IHVG_ENSURE_DEFAULTS = gql`
  ${ADMIN_IHVG_PACK_FIELDS}
  mutation AdminIhvgEnsureDefaults($refill: Boolean = false) {
    adminIhvgEnsureDefaults(refill: $refill) {
      skippedCount
      created {
        ...AdminIhvgPackFields
      }
      composed {
        ...AdminIhvgPackFields
      }
      refilled {
        ...AdminIhvgPackFields
      }
      proofs {
        id
        objectKey
        label
        surface
        source
      }
      clips {
        id
        label
        proofId
        suggestedCaption
        suggestedCta
      }
    }
  }
`;

export const ADMIN_IHVG_IMPORT_ASSET = gql`
  ${ADMIN_IHVG_PACK_FIELDS}
  mutation AdminIhvgImportAsset($input: AdminIhvgImportAssetInput!) {
    adminIhvgImportAsset(input: $input) {
      kind
      message
      proof {
        id
        objectKey
        label
        surface
        source
      }
      pack {
        ...AdminIhvgPackFields
      }
      audioBed {
        id
        objectKey
        label
        licenseRef
        mime
      }
    }
  }
`;

export const ADMIN_IHVG_GENERATE_PACK = gql`
  ${ADMIN_IHVG_PACK_FIELDS}
  mutation AdminIhvgGeneratePack($input: AdminIhvgGeneratePackInput!) {
    adminIhvgGeneratePack(input: $input) {
      ...AdminIhvgPackFields
    }
  }
`;

export const ADMIN_IHVG_UPDATE_PACK_DRAFT = gql`
  ${ADMIN_IHVG_PACK_FIELDS}
  mutation AdminIhvgUpdatePackDraft($id: ID!, $input: AdminIhvgUpdatePackDraftInput!) {
    adminIhvgUpdatePackDraft(id: $id, input: $input) {
      ...AdminIhvgPackFields
    }
  }
`;

export const ADMIN_IHVG_APPROVE_PACK = gql`
  ${ADMIN_IHVG_PACK_FIELDS}
  mutation AdminIhvgApprovePack($id: ID!) {
    adminIhvgApprovePack(id: $id) {
      ...AdminIhvgPackFields
    }
  }
`;

export const ADMIN_IHVG_SCHEDULE_PACK = gql`
  ${ADMIN_IHVG_PACK_FIELDS}
  mutation AdminIhvgSchedulePack($input: AdminIhvgSchedulePackInput!) {
    adminIhvgSchedulePack(input: $input) {
      ...AdminIhvgPackFields
    }
  }
`;

export const ADMIN_IHVG_RETRY_PUBLISH = gql`
  ${ADMIN_IHVG_PACK_FIELDS}
  mutation AdminIhvgRetryPublish($packId: ID!, $platforms: [AdminIhvgPlatform!]) {
    adminIhvgRetryPublish(packId: $packId, platforms: $platforms) {
      ...AdminIhvgPackFields
    }
  }
`;
